import { FunctionComponent, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import UserForm from "../components/SignUpForms/UserForm";
import UnAuthenticatedLayout from "../layouts/UnAuthenticatedLayout";
import BusinessForm from "../components/SignUpForms/BusinessForm";
import { isEmpty } from "../lib/helpers";
import {
  useAuthState,
  useCreateUserWithEmailAndPassword,
} from "react-firebase-hooks/auth";
import { auth, db } from "../config/firebaseConfig";
import { useCreateBusiness } from "@/hooks/queries/use-business";
import { useProfile } from "../context/ProfileContext";
import {
  AuthCard,
  AuthCardContainer,
  AuthCardHeader,
  AuthCardLogo,
  AuthCardTitle,
} from "@/components/ui/auth-card";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { BusinessMember } from "@/types/businessMember";
import { createCheckoutSession } from "@/services/stripeApi";
import { Button } from "@/components/ui/button";
import { Crown, Sparkles, CheckCircle2 } from "lucide-react";
import {
  trackOnboardingStarted,
  trackUserRegistrationCompleted,
  trackUserRegistrationError,
  trackBusinessRegistrationStarted,
  trackBusinessRegistrationCompleted,
  trackBusinessRegistrationError,
  trackStripeCheckoutView,
  trackStripeCheckoutInitiated,
  trackStripeCheckoutError,
  trackOnboardingCompleted,
  setUserId,
} from "@/lib/analytics";

interface CreateTrialPageProps {
  gotoStep?: number;
}

declare global {
  interface Window {
    dataLayer: any[];
  }
}

// TODO: Implement gotoStep
const OnboardingView: FunctionComponent<
  CreateTrialPageProps
> = (/* { gotoStep } */) => {
  // Core states
  const { redirectStep } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check for step in query params (for cancel flow) or route params
  const initialStep = searchParams.get("step")
    ? Number.parseInt(searchParams.get("step")!)
    : redirectStep
    ? Number.parseInt(redirectStep)
    : 1;

  const [step, setStep] = useState<number>(initialStep);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>();
  const [userData, setUserData] = useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);

  const [user /* loadingUser, errorUser */] = useAuthState(auth);
  const [
    createUserWithEmailAndPassword /* , userCredential, loading, error */,
  ] = useCreateUserWithEmailAndPassword(auth);
  const { setProfileCreated, businessProfile } = useProfile();

  const { mutateAsync: createBusiness } = useCreateBusiness();

  // Check if returning from successful Stripe checkout
  const onboardingSuccess = searchParams.get("onboarding") === "success";

  // Redirect to home if user has completed onboarding (has business profile) or returning from successful checkout
  useEffect(() => {
    if (user && (businessProfile || onboardingSuccess)) {
      // Track onboarding completion if returning from successful checkout
      if (onboardingSuccess && user.uid) {
        trackOnboardingCompleted(user.uid, user.uid);
      }
      navigate("/");
    }
  }, [user, businessProfile, onboardingSuccess, navigate]);

  // Auto-advance to step 2 if user is logged in and on step 1
  useEffect(() => {
    if (user && step === 1 && !businessProfile) {
      trackBusinessRegistrationStarted(user.uid);
      setStep(2);
    }
  }, [user, step, businessProfile]);

  // Track onboarding started on initial mount (step 1)
  useEffect(() => {
    if (initialStep === 1) {
      trackOnboardingStarted();
    } else if (initialStep === 3 && user?.uid) {
      // Returning from cancelled Stripe checkout
      trackStripeCheckoutView(user.uid, user.uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set businessId from user.uid if not set and user exists (for cancel flow)
  useEffect(() => {
    if (user && !businessId && step === 3) {
      setBusinessId(user.uid);
    }
  }, [user, businessId, step]);

  // Step 1: Register user credentials
  const registerUser = async (formData: any) => {
    const { name, email, password, passwordRepeat } = formData;

    if (
      isEmpty(name) ||
      isEmpty(email) ||
      isEmpty(password) ||
      isEmpty(passwordRepeat)
    ) {
      setErrorMessage("Fyll i alla fält!");
      return;
    } else if (password !== passwordRepeat) {
      setErrorMessage("Lösenorden matchar inte.");
      return;
    }

    try {
      setStep(0);
      const userCredential = await createUserWithEmailAndPassword(
        email,
        password
      );

      if (userCredential?.user) {
        // Set user ID for GA4 tracking
        setUserId(userCredential.user.uid);

        // Track successful registration
        trackUserRegistrationCompleted(userCredential.user.uid, email);
      }

      // Store user data for later use
      setUserData({ name, email, password });
      setErrorMessage(null);

      // Track step transition before changing step
      if (userCredential?.user) {
        trackBusinessRegistrationStarted(userCredential.user.uid);
      }
      setStep(2);
    } catch (error: any) {
      setStep(1);
      setErrorMessage(error.message);

      // Track registration error
      trackUserRegistrationError(error.message);

      console.error("Error creating user", { error });
    }
  };

  // Step 2: Register business credentials
  const registerBusiness = async (formData: any) => {
    if (!user || !userData) return;

    const { company, companyNumber } = formData;

    const businessDataToCreate = {
      business: company,
      orgNumber: companyNumber,
      name: userData.name, // Use name from step 1
      email: user.email || userData.email,
    };

    try {
      setLoading(true);

      // Create business document
      await createBusiness({ business: businessDataToCreate, uid: user.uid });

      // Create BusinessMember document
      const now = serverTimestamp();
      const businessMemberRef = doc(db, "BusinessMember", user.uid);
      const memberData: BusinessMember = {
        uid: user.uid,
        name: userData.name,
        email: user.email || userData.email,
        role: "admin",
        invitedAt: now as any,
        createdAt: now as any,
        updatedAt: now as any,
      };

      await setDoc(businessMemberRef, memberData);

      // Create member entry in business subcollection
      const businessMemberSubRef = doc(
        db,
        "Businesses",
        user.uid,
        "members",
        user.uid
      );
      await setDoc(businessMemberSubRef, {
        uid: user.uid,
        name: userData.name,
        email: user.email || userData.email,
        role: "admin",
        joinedAt: now,
      });

      setBusinessId(user.uid);
      setErrorMessage(null);

      // Track successful business registration and step transition
      trackBusinessRegistrationCompleted(user.uid, user.uid, company);
      trackStripeCheckoutView(user.uid, user.uid);

      setStep(3); // Move to Stripe signup step
    } catch (error: any) {
      console.error("Error creating business", error);
      const errorMsg = "Kunde inte skapa företagskonto. Vänligen försök igen.";
      setErrorMessage(errorMsg);

      // Track business registration error
      trackBusinessRegistrationError(user.uid, error?.message || errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Handle Stripe trial signup
  const handleStripeSignup = async () => {
    if (!user || !businessId) {
      setErrorMessage(
        "Kunde inte hitta företagsuppgifter. Vänligen försök igen."
      );
      return;
    }

    setLoading(true);

    // Track stripe checkout initiated
    trackStripeCheckoutInitiated(user.uid, businessId);

    try {
      const { checkoutUrl } = await createCheckoutSession({
        businessId: businessId,
        successUrl: `${window.location.origin}/?onboarding=success`,
        cancelUrl: `${window.location.origin}/onboarding?step=3`,
        customerEmail: user.email || userData?.email,
        trialPeriodDays: 7, // 14-day trial
        allowPromotionCodes: true,
      });

      // Mark profile as created before redirecting
      if (setProfileCreated) {
        setProfileCreated(true);
      }

      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
    } catch (error: any) {
      console.error("Failed to create checkout session:", error);
      const errorMsg =
        "Kunde inte öppna betalningssidan. Vänligen försök igen.";
      setErrorMessage(errorMsg);

      // Track stripe checkout error
      trackStripeCheckoutError(
        user.uid,
        businessId,
        error?.message || errorMsg
      );

      setLoading(false);
    }
  };

  return (
    <UnAuthenticatedLayout>
      <AuthCard maxWidth="max-w-[600px]">
        <AuthCardHeader>
          <AuthCardLogo
            src="https://graftus.ai/wp-content/themes/seodr/assets/img/logo.svg"
            alt="Graftus AI"
          />
          <AuthCardTitle>Skapa ditt konto</AuthCardTitle>
        </AuthCardHeader>

        <AuthCardContainer>
          {step === 0 ? (
            <div className="text-center p-8">
              <h2 className="text-white/80 text-xl mb-4">Laddar...</h2>
              <div className="flex justify-center">
                <Spin size="large" />
              </div>
            </div>
          ) : step === 1 ? (
            <UserForm
              formRef={{} as any}
              handleClick={registerUser}
              errorMsg={errorMessage!}
            />
          ) : step === 2 ? (
            <BusinessForm
              formRef={{} as any}
              handleClick={registerBusiness}
              errorMsg={errorMessage!}
              loading={loading}
            />
          ) : step === 3 ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-full p-4">
                    <Crown className="h-10 w-10 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-semibold text-white/90 mb-2">
                  Starta din 7-dagars gratis provperiod
                </h2>
                <p className="text-white/70 text-base mb-6">
                  Få full tillgång till alla Pro-funktioner utan kortuppgifter.
                </p>
              </div>

              {errorMessage && (
                <div className="rounded-xl border border-[rgba(255,107,107,0.3)] bg-[rgba(255,107,107,0.1)] p-4">
                  <div className="text-[#ff6b6b] font-semibold text-sm">
                    {errorMessage}
                  </div>
                </div>
              )}

              <div className="bg-white/10 rounded-xl p-6 pb-2 space-y-4">
                <h3 className="text-white/90 font-semibold text-lg mb-4">
                  Vad ingår i Graftus Recruit?
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white/90 font-medium">
                        AI-annonsflöde
                      </p>
                      <p className="text-white/60 text-sm">
                        skapa jobbannonser på 30 sekunder
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white/90 font-medium">
                        Avancerad matchning
                      </p>
                      <p className="text-white/60 text-sm">
                        Få fram toppkandidater direkt med AI-matchning
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white/90 font-medium">
                        Obegränsade annonser
                      </p>
                      <p className="text-white/60 text-sm">
                        Publicera hur många jobb du vill
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t border-white/10 mt-4">
                  <p className="text-white/60 text-sm text-center">
                    Över 17 000 säljare i plattformen
                  </p>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 pb-1 border border-white/10">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">
                    Du kan avsluta när som helst under provperioden utan
                    kostnad. Ingen kortinformation krävs.
                  </p>
                </div>
              </div>

              <Button
                onClick={handleStripeSignup}
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 border-none rounded-xl h-12 text-base font-semibold transition-all duration-300 hover:transform hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(251,191,36,0.3)] active:transform-none disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <>
                    <Spin size="small" className="mr-2" />
                    Öppnar betalningssidan...
                  </>
                ) : (
                  <>
                    <Crown className="h-5 w-5 mr-2" />
                    Starta gratis provperiod
                  </>
                )}
              </Button>

              <p className="text-center text-white/50 text-xs">
                Säker betalning via Stripe
              </p>
            </div>
          ) : null}
        </AuthCardContainer>
      </AuthCard>
    </UnAuthenticatedLayout>
  );
};

export default OnboardingView;
